import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Users, Building2, TrendingUp, Filter, UserPlus, Eye, ChevronRight, Newspaper, Zap, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import axios from 'axios';
import { getPartyConfig } from '@/lib/theme';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live Article Counter Component
const LiveArticleCounter = () => {
  const [stats, setStats] = useState(null);
  const [prevCount, setPrevCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await axios.get(`${API}/councillor-articles/stats`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        const newStats = response.data;
        
        // Trigger animation if count increased
        if (newStats.total_articles > prevCount && prevCount > 0) {
          setIsAnimating(true);
          setTimeout(() => setIsAnimating(false), 1000);
        }
        
        setPrevCount(newStats.total_articles);
        setStats(newStats);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch article stats:', error);
        setLoading(false);
        // Set default stats on error
        if (!stats) {
          setStats({
            total_articles: 0,
            total_councillors: 19107,
            articles_last_hour: 0,
            latest_articles: [],
            scraping_active: true
          });
        }
      }
    };

    fetchStats();
    // Poll every 15 seconds for live updates (reduced frequency)
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, [prevCount, stats]);

  // Show loading skeleton or fallback
  if (loading && !stats) {
    return (
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white relative overflow-hidden" data-testid="live-article-counter">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            <span className="font-semibold">Live News Scanning</span>
          </div>
          <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm">
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span>Loading...</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white/10 rounded-xl p-3 text-center animate-pulse">
              <div className="h-8 bg-white/20 rounded mb-2" />
              <div className="h-3 bg-white/20 rounded w-20 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white relative overflow-hidden" data-testid="live-article-counter">
      {/* Animated background pulse when new articles arrive */}
      {isAnimating && (
        <div className="absolute inset-0 bg-white/20 animate-ping rounded-2xl" />
      )}
      
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            <span className="font-semibold">Live News Scanning</span>
          </div>
          {stats.scraping_active && (
            <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm">
              <RefreshCw className="h-3 w-3 animate-spin" />
              <span>Active</span>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className={`text-3xl font-black ${isAnimating ? 'scale-110 transition-transform' : ''}`}>
              {stats.total_articles.toLocaleString()}
            </div>
            <div className="text-xs text-white/80">Councillor Articles</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-3xl font-black">{stats.total_councillors.toLocaleString()}</div>
            <div className="text-xs text-white/80">Councillors Tracked</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-3xl font-black flex items-center justify-center gap-1">
              {stats.articles_last_hour > 0 && <Zap className="h-5 w-5 text-yellow-300" />}
              {stats.articles_last_hour}
            </div>
            <div className="text-xs text-white/80">Added Last Hour</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-3xl font-black">649</div>
            <div className="text-xs text-white/80">MPs Tracked</div>
          </div>
        </div>
        
        {/* Latest articles ticker */}
        {stats.latest_articles && stats.latest_articles.length > 0 && (
          <div className="border-t border-white/20 pt-4">
            <div className="text-xs text-white/70 mb-2">Latest councillor news:</div>
            <div className="space-y-1">
              {stats.latest_articles.slice(0, 3).map((article, i) => (
                <div key={i} className="text-sm truncate flex items-center gap-2">
                  <span className="text-emerald-200">{article.mentioned_councillor_names?.[0] || 'Unknown'}</span>
                  <span className="text-white/60">-</span>
                  <span className="text-white/80 truncate">{article.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Top councillors in news */}
        {stats.top_councillors && stats.top_councillors.length > 0 && (
          <div className="border-t border-white/20 pt-4 mt-4">
            <div className="text-xs text-white/70 mb-2">Most newsworthy councillors:</div>
            <div className="flex flex-wrap gap-2">
              {stats.top_councillors.map((c, i) => (
                <span key={i} className="bg-white/20 px-2 py-1 rounded-full text-xs">
                  {c.name} ({c.count})
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PoliticiansPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { hasFullAccess } = useSubscription();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [partyFilter, setPartyFilter] = useState(searchParams.get('party') || '');

  // Fetch trending politicians on mount
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const response = await axios.get(`${API}/politicians/trending`);
        setTrending(response.data.trending || []);
      } catch (error) {
        console.error('Failed to fetch trending:', error);
      }
    };
    fetchTrending();
  }, []);

  // Search politicians
  const searchPoliticians = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: query });
      if (typeFilter) params.append('type', typeFilter);
      if (partyFilter) params.append('party', partyFilter);
      
      const response = await axios.get(`${API}/politicians/search?${params}`);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, partyFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchPoliticians(searchQuery);
        setSearchParams({ q: searchQuery, ...(typeFilter && { type: typeFilter }), ...(partyFilter && { party: partyFilter }) });
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, searchPoliticians, setSearchParams, typeFilter, partyFilter]);

  const parties = [
    { id: '', label: 'All Parties' },
    { id: 'labour', label: 'Labour' },
    { id: 'conservative', label: 'Conservative' },
    { id: 'libdem', label: 'Liberal Democrats' },
    { id: 'snp', label: 'SNP' },
    { id: 'reform', label: 'Reform UK' },
    { id: 'green', label: 'Green' },
    { id: 'plaid', label: 'Plaid Cymru' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="politicians-page">
      <SEO 
        title="Search UK Politicians - MPs & Councillors Database"
        description="Search and track any UK politician. Complete database of 649 MPs and 19,000+ councillors. Find your local representative, view their news coverage and political record."
        keywords="UK MPs list, find my MP, UK councillors, local politicians, search politicians UK, MP database, councillor search, track politicians"
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            Search UK Politicians
          </h1>
          <p className="text-lg text-slate-300 mb-8">
            Research any of the 649 MPs and 19,000+ councillors across the UK
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, constituency, or council..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg bg-white text-slate-900 border-0 rounded-xl shadow-lg"
              data-testid="politician-search-input"
            />
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600"
            >
              <option value="">All Types</option>
              <option value="mp">MPs Only</option>
              <option value="councillor">Councillors Only</option>
            </select>
            
            <select
              value={partyFilter}
              onChange={(e) => setPartyFilter(e.target.value)}
              className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600"
            >
              {parties.map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Live Article Counter */}
        <div className="mb-8">
          <LiveArticleCounter />
        </div>

        {/* Search Results */}
        {searchQuery.length >= 2 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Results
              {results.length > 0 && <span className="text-slate-500">({results.length})</span>}
            </h2>
            
            {loading ? (
              <div className="text-center py-8 text-slate-500">Searching...</div>
            ) : results.length === 0 ? (
              <div className="text-center py-8 text-slate-500">No politicians found for "{searchQuery}"</div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.map((politician) => (
                  <PoliticianCard key={politician.slug} politician={politician} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trending Politicians */}
        {trending.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              Trending Politicians
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trending.slice(0, 6).map((politician, idx) => {
                const slug = politician.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
                return (
                  <Link 
                    key={idx}
                    to={`/politician/${slug}`}
                    className="bg-white rounded-xl p-4 border border-slate-200 hover:shadow-lg hover:border-orange-300 transition-all group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold group-hover:text-orange-600 transition-colors">{politician.name}</span>
                      <span className="text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                        {politician.mentions} mentions
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mb-2">
                      {politician.politician_type} • {politician.party}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{politician.latest_headline}</p>
                    <div className="mt-2 text-xs text-orange-500 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      View Profile <ChevronRight className="h-3 w-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Link to="/politicians?type=MP" className="bg-white rounded-xl p-6 text-center border border-slate-200 hover:shadow-lg hover:border-blue-300 transition-all group">
            <Users className="h-8 w-8 mx-auto mb-2 text-blue-500 group-hover:scale-110 transition-transform" />
            <div className="text-3xl font-black">649</div>
            <div className="text-slate-500">MPs</div>
            <div className="text-xs text-blue-500 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Browse MPs →</div>
          </Link>
          <Link to="/politicians?type=Councillor" className="bg-white rounded-xl p-6 text-center border border-slate-200 hover:shadow-lg hover:border-purple-300 transition-all group">
            <Building2 className="h-8 w-8 mx-auto mb-2 text-purple-500 group-hover:scale-110 transition-transform" />
            <div className="text-3xl font-black">19,107</div>
            <div className="text-slate-500">Councillors</div>
            <div className="text-xs text-purple-500 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Browse Councillors →</div>
          </Link>
          <Link to="/map" className="bg-white rounded-xl p-6 text-center border border-slate-200 hover:shadow-lg hover:border-emerald-300 transition-all group">
            <Building2 className="h-8 w-8 mx-auto mb-2 text-emerald-500 group-hover:scale-110 transition-transform" />
            <div className="text-3xl font-black">382</div>
            <div className="text-slate-500">Councils</div>
            <div className="text-xs text-emerald-500 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View Map →</div>
          </Link>
          <Link to="/watchlist" className="bg-white rounded-xl p-6 text-center border border-slate-200 hover:shadow-lg hover:border-amber-300 transition-all group">
            <Eye className="h-8 w-8 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
            <div className="text-3xl font-black">24/7</div>
            <div className="text-slate-500">Monitoring</div>
            <div className="text-xs text-amber-500 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Your Watchlist →</div>
          </Link>
        </div>

        {/* Browse by Party */}
        <div>
          <h2 className="text-xl font-bold mb-4">Browse by Party</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {parties.slice(1).map(party => {
              const config = getPartyConfig(party.id);
              return (
                <Link
                  key={party.id}
                  to={`/politicians?party=${party.id}`}
                  className="p-4 rounded-xl border-2 hover:shadow-lg transition-all flex items-center gap-3"
                  style={{ borderColor: config.color + '40', backgroundColor: config.color + '10' }}
                >
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: config.color }}
                  />
                  <span className="font-medium">{party.label}</span>
                  <ChevronRight className="h-4 w-4 ml-auto text-slate-400" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const PoliticianCard = ({ politician }) => {
  const { token } = useAuth();
  const partyConfig = getPartyConfig(politician.party);
  const [isWatching, setIsWatching] = useState(false);

  const handleWatch = async (e) => {
    e.preventDefault();
    if (!token) return;
    
    try {
      await axios.post(`${API}/watchlist/add`, {
        politician_name: politician.name,
        politician_type: politician.politician_type,
        constituency: politician.constituency,
        council: politician.council,
        party: politician.party
      }, { headers: { Authorization: `Bearer ${token}` }});
      setIsWatching(true);
    } catch (error) {
      console.error('Failed to add to watchlist:', error);
    }
  };

  return (
    <Link
      to={`/politician/${politician.slug}`}
      className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all block"
      data-testid={`politician-card-${politician.slug}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-bold text-lg">{politician.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span 
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ 
                backgroundColor: partyConfig.color + '20', 
                color: partyConfig.color 
              }}
            >
              {partyConfig.shortName || politician.party}
            </span>
            <span className="text-xs text-slate-500">{politician.politician_type}</span>
          </div>
        </div>
        {token && !isWatching && (
          <button
            onClick={handleWatch}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Add to watchlist"
          >
            <UserPlus className="h-4 w-4 text-slate-400" />
          </button>
        )}
      </div>
      
      <p className="text-sm text-slate-600">
        {politician.constituency || politician.council || politician.ward}
      </p>
      
      {politician.region && (
        <p className="text-xs text-slate-400 mt-1">{politician.region}</p>
      )}
    </Link>
  );
};

export default PoliticiansPage;
